import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import type { BankAccountListItem } from "Infrastructure/Api/Api";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, useResource, type TFunction } from "Translations/Resources";

const UNASSIGNED_CONTEXT_KEY = "unassigned";

export type ContextGroup = {
  key: string;
  label: string;
  accounts: Array<BankAccountListItem>;
};

const getContextLabel = (
  account: BankAccountListItem,
  t: TFunction,
): string => {
  const { contextRoleCode, contextPartyName } = account;

  if (!contextRoleCode) {
    return t(
      Resources.Pages.Content.Users.ConnectedBankAccounts.UnassignedContext,
    );
  }

  const roleLabel = isAccessRoleCode(contextRoleCode)
    ? t(Resources.User.AccessRole[contextRoleCode])
    : contextRoleCode;

  return contextPartyName ? `${roleLabel} — ${contextPartyName}` : roleLabel;
};

// Keyed on role AND party: one identity can hold several grants of the same role for different
// companies, and merging those would file accounts under the wrong company.
// Unassigned rows (connected before accounts were role-scoped) sort last — they are the leftover,
// not a role of their own.
const getContextKey = ({
  contextRoleCode,
  contextPartyName,
}: BankAccountListItem): string =>
  contextRoleCode
    ? `${contextRoleCode}|${contextPartyName ?? ""}`
    : UNASSIGNED_CONTEXT_KEY;

const groupByRoleContext = (
  accounts: Array<BankAccountListItem>,
  t: TFunction,
): Array<ContextGroup> => {
  const groups: Array<ContextGroup> = [];

  for (const account of accounts) {
    const key = getContextKey(account);
    const existing = groups.find((group) => group.key === key);

    if (existing) {
      existing.accounts.push(account);
      continue;
    }

    groups.push({
      key,
      label: getContextLabel(account, t),
      accounts: [account],
    });
  }

  return groups.sort((a, b) => {
    if (a.key === b.key) {
      return 0;
    }
    if (a.key === UNASSIGNED_CONTEXT_KEY) {
      return 1;
    }
    return b.key === UNASSIGNED_CONTEXT_KEY ? -1 : 0;
  });
};

export const useGroupedBankingAccounts = (userID: number) => {
  const { data, isLoading, isError } = useBankingAccounts(userID);
  const { t } = useResource();
  const accounts = data?.items;
  const groups = accounts ? groupByRoleContext(accounts, t) : [];
  // A single labeled group still gets its header: back-office needs to see which role context owns the
  // connections even when the person only has one. Only a lone unassigned group says nothing new.
  const hasLabeledGroups = groups.some(
    ({ key }) => key !== UNASSIGNED_CONTEXT_KEY,
  );

  return { data, accounts, groups, hasLabeledGroups, isLoading, isError };
};
